<template>
  <v-container
    fluid
    :class="[
      'admin-dashboard',
      $vuetify.dark ? 'admin-dashboard--dark' : 'admin-dashboard--light',
    ]"
  >
    <v-layout row wrap>
      <v-flex xs12>
        <v-card class="mb-3 rounded-card admin-migration-card">
          <v-toolbar color="red" dark dense>
            <v-toolbar-title
              >Match migration — canonical copies (phase 1)</v-toolbar-title
            >
          </v-toolbar>
          <v-card-text>
            <p class="admin-section-desc">
              Merges legacy
              <code>athletes/…/matches/…</code> into
              <code>matches_canonical/{id}</code> in small batches (checkpoint
              in <code>_migrations/match_canonical_v1</code>). Safe to re-run;
              does <strong>not</strong> delete old docs. Uses your project’s
              Firestore reads/writes — keep batch size low if you hit quotas.
            </p>
            <p class="admin-section-desc">
              <strong>From the app:</strong> deploy Cloud Functions once (Blaze),
              deploy the new index, sign in as admin, then run batches here.
              <strong>From your PC:</strong>
              <code class="admin-inline-code">npm run migrate:canonical-batch</code>
              with service-account or gcloud credentials (no Console needed for
              each batch).
            </p>
            <v-layout row wrap align-end>
              <v-flex xs12 sm4 md3 class="pr-sm-2">
                <v-text-field
                  v-model.number="migrationBatchSize"
                  class="admin-migration-batch-field"
                  type="number"
                  label="Docs per batch"
                  min="5"
                  max="200"
                  hint="Each run is capped at 200 legacy match docs (Cloud Function limit)."
                  persistent-hint
                  outline
                  dense
                  :dark="$vuetify.dark"
                />
              </v-flex>
              <v-flex xs12 sm8 md9>
                <v-btn
                  color="primary"
                  class="ma-0"
                  :loading="migrationLoading"
                  :disabled="migrationLoading"
                  @click="runCanonicalMigrationBatch"
                >
                  <v-icon left small>play_arrow</v-icon>
                  Run one batch
                </v-btn>
              </v-flex>
            </v-layout>
            <pre
              v-if="migrationLastResultJson"
              class="admin-migration-result caption"
            >{{ migrationLastResultJson }}</pre>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12>
        <v-card class="mb-3 rounded-card">
          <v-toolbar color="red" dark>
            <v-toolbar-title>Registered users</v-toolbar-title>
            <v-spacer />
            <v-btn
              icon
              dark
              :loading="usersLoading"
              :disabled="usersLoading"
              @click="loadUsers"
            >
              <v-icon>refresh</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <p class="admin-section-desc">
              Users who have signed in (or been saved to the users collection).
              Last login is updated on each successful sign-in.
            </p>

            <div class="admin-table-shell">
              <v-data-table
                :headers="userHeaders"
                :items="usersTableItems"
                :loading="usersLoading"
                item-key="uid"
                class="admin-data-table"
                :rows-per-page-items="[10, 25, 50, 100]"
                :pagination.sync="usersPagination"
              >
                <template slot="items" slot-scope="props">
                  <tr class="admin-table-row">
                    <td>{{ props.item._name }}</td>
                    <td class="admin-cell-mono">
                      {{ props.item.email || "—" }}
                    </td>
                    <td>
                      <v-chip
                        v-if="props.item.providerId"
                        class="admin-provider-chip"
                        small
                        outline
                      >
                        {{ props.item.providerId }}
                      </v-chip>
                      <span v-else class="admin-dash">—</span>
                    </td>
                    <td class="admin-cell-nowrap">{{ props.item._created }}</td>
                    <td class="admin-cell-nowrap admin-cell-emphasis">
                      {{ props.item._login }}
                    </td>
                  </tr>
                </template>
                <template slot="no-data">
                  <div class="admin-empty">
                    {{ usersLoading ? "Loading…" : "No users found." }}
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12>
        <v-card class="mb-3 rounded-card">
          <v-toolbar color="red" dark>
            <v-toolbar-title>Wrestler profile audit</v-toolbar-title>
            <v-spacer />
            <v-btn
              icon
              dark
              :loading="auditLoading"
              :disabled="auditLoading"
              @click="loadAuditLogs"
            >
              <v-icon>refresh</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <p class="admin-section-desc">
              Changes to athlete profiles: who made the change, which wrestler,
              and what fields changed.
            </p>
            <div class="admin-table-shell">
              <v-data-table
                :headers="wrestlerAuditHeaders"
                :items="wrestlerAuditItems"
                :loading="auditLoading"
                item-key="id"
                class="admin-data-table"
                :rows-per-page-items="[10, 25, 50, 100]"
                :pagination.sync="wrestlerAuditPagination"
              >
                <template slot="items" slot-scope="props">
                  <tr class="admin-table-row">
                    <td class="admin-cell-nowrap">{{ props.item._when }}</td>
                    <td
                      class="admin-cell-by"
                      :title="changedByTitle(props.item)"
                    >
                      {{ props.item._by }}
                    </td>
                    <td>
                      <div class="admin-cell-stack">
                        <span
                          :title="
                            athleteNameById[props.item.entityId]
                              ? props.item.entityId
                              : ''
                          "
                          >{{ props.item._wrestler }}</span
                        >
                        <code
                          v-if="wrestlerShowRawIdHint(props.item)"
                          class="admin-id-code"
                          >{{ props.item.entityId }}</code
                        >
                      </div>
                    </td>
                    <td>
                      <v-chip
                        :color="getActionColor(props.item.action)"
                        class="admin-action-chip"
                        dark
                        small
                      >
                        {{ props.item.action }}
                      </v-chip>
                    </td>
                    <td>
                      <div
                        class="changes-cell"
                        :title="formatChangesDetail(props.item.changes, true)"
                      >
                        {{ formatChangesDetail(props.item.changes, false) }}
                      </div>
                    </td>
                    <td class="admin-actions-cell">
                      <v-btn
                        icon
                        small
                        aria-label="Delete audit entry"
                        :disabled="auditDeleting"
                        @click="openAuditDeleteDialog(props.item, 'wrestler')"
                      >
                        <v-icon small color="grey darken-1">delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </template>
                <template slot="no-data">
                  <div class="admin-empty">
                    {{
                      auditLoading
                        ? "Loading…"
                        : "No wrestler profile changes recorded."
                    }}
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12>
        <v-card class="rounded-card">
          <v-toolbar color="red" dark>
            <v-toolbar-title>Match audit</v-toolbar-title>
            <v-spacer />
            <v-btn
              icon
              dark
              :loading="auditLoading"
              :disabled="auditLoading"
              @click="loadAuditLogs"
            >
              <v-icon>refresh</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <p class="admin-section-desc">
              Match create/update/delete events: who changed what and when.
              Older “create” entries used the wrestler’s ID as the entity key;
              updates use the match document ID under that wrestler.
            </p>
            <div class="admin-table-shell">
              <v-data-table
                :headers="matchAuditHeaders"
                :items="matchAuditItems"
                :loading="auditLoading"
                item-key="id"
                class="admin-data-table"
                :rows-per-page-items="[10, 25, 50, 100]"
                :pagination.sync="matchAuditPagination"
              >
                <template slot="items" slot-scope="props">
                  <tr class="admin-table-row">
                    <td class="admin-cell-nowrap">{{ props.item._when }}</td>
                    <td
                      class="admin-cell-by"
                      :title="changedByTitle(props.item)"
                    >
                      {{ props.item._by }}
                    </td>
                    <td>
                      <div class="admin-cell-stack">
                        <span>{{ props.item._subject }}</span>
                        <code
                          v-if="matchShowRawIdHint(props.item)"
                          class="admin-id-code"
                          >{{ props.item.entityId }}</code
                        >
                      </div>
                    </td>
                    <td>
                      <v-chip
                        :color="getActionColor(props.item.action)"
                        class="admin-action-chip"
                        dark
                        small
                      >
                        {{ props.item.action }}
                      </v-chip>
                    </td>
                    <td>
                      <div
                        class="changes-cell"
                        :title="formatChangesDetail(props.item.changes, true)"
                      >
                        {{ formatChangesDetail(props.item.changes, false) }}
                      </div>
                    </td>
                    <td class="admin-actions-cell">
                      <v-btn
                        icon
                        small
                        aria-label="Delete audit entry"
                        :disabled="auditDeleting"
                        @click="openAuditDeleteDialog(props.item, 'match')"
                      >
                        <v-icon small color="grey darken-1">delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </template>
                <template slot="no-data">
                  <div class="admin-empty">
                    {{
                      auditLoading ? "Loading…" : "No match changes recorded."
                    }}
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-dialog v-model="auditDeleteDialog" max-width="440">
      <v-card
        class="admin-delete-dialog-card"
        :dark="$vuetify.dark"
      >
        <v-card-title class="headline">Delete audit entry?</v-card-title>
        <v-card-text>
          <p class="mb-2">
            This removes one row from the audit log permanently. You cannot undo
            this.
          </p>
          <div v-if="auditDeleteTarget" class="admin-delete-preview caption">
            <div>
              <strong>Log</strong>:
              {{ auditDeleteSectionLabel }}
            </div>
            <div v-if="auditDeleteTarget._when">
              <strong>When</strong>: {{ auditDeleteTarget._when }}
            </div>
            <div v-if="auditDeleteTarget.action">
              <strong>Action</strong>: {{ auditDeleteTarget.action }}
            </div>
            <div v-if="auditDeleteTarget.entityId">
              <strong>Entity ID</strong>:
              <code class="admin-id-code admin-id-code--inline">{{
                auditDeleteTarget.entityId
              }}</code>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat :disabled="auditDeleting" @click="closeAuditDeleteDialog">
            Cancel
          </v-btn>
          <v-btn
            color="error"
            flat
            :loading="auditDeleting"
            @click="confirmDeleteAuditEntry"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <FloatingNav />
  </v-container>
</template>

<script>
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getFirestore,
  collection,
  collectionGroup,
  getDocs,
  getDoc,
  doc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import FloatingNav from "../components/FloatingNav.vue";
import {
  wrestlerDisplayNameFromAuditChanges,
  matchSummaryFromAuditChanges,
} from "../utils/auditLog";
import { MATCHES_CANONICAL_COLLECTION } from "../utils/canonicalMatches";

const FIELD_LABELS = {
  FirstName: "First name",
  LastName: "Last name",
  FullName: "Full name",
  School: "School",
  Grade: "Grade",
  RankLeague: "League",
  RankWeightClass: "Weight class",
  Skill: "Skill",
  Wins: "Wins",
  Losses: "Losses",
  Location: "Location",
  MatchDate: "Match date",
  Season: "Season",
  OpponentFullName: "Opponent",
  OpponentName: "Opponent",
  Result: "Result",
  Type: "Type",
  VideoLink: "Video",
  WrestlerID: "Linked wrestler ID",
  SkillChange: "Skill change",
};

export default {
  name: "AdminDashboard",
  components: { FloatingNav },
  data() {
    return {
      users: [],
      usersLoading: false,
      allAuditLogs: [],
      auditLoading: false,
      athleteNameById: {},
      matchMetaById: {},
      userHeaders: [
        { text: "Name", value: "_sortName", align: "left" },
        { text: "Email", value: "email", align: "left" },
        {
          text: "Provider",
          value: "providerId",
          align: "left",
          sortable: false,
        },
        { text: "Created", value: "_createdMs", align: "left" },
        { text: "Last login", value: "_sortLogin", align: "left" },
      ],
      wrestlerAuditHeaders: [
        { text: "When", value: "_whenMs", align: "left" },
        { text: "Changed by", value: "_by", align: "left" },
        { text: "Wrestler", value: "_wrestler", align: "left" },
        { text: "Action", value: "action", align: "left" },
        {
          text: "What changed",
          value: "_changeSort",
          align: "left",
          sortable: false,
        },
        {
          text: "Actions",
          value: "_actions",
          align: "center",
          sortable: false,
        },
      ],
      matchAuditHeaders: [
        { text: "When", value: "_whenMs", align: "left" },
        { text: "Changed by", value: "_by", align: "left" },
        { text: "Subject", value: "_subject", align: "left" },
        { text: "Action", value: "action", align: "left" },
        {
          text: "What changed",
          value: "_changeSort",
          align: "left",
          sortable: false,
        },
        {
          text: "Actions",
          value: "_actions",
          align: "center",
          sortable: false,
        },
      ],
      usersPagination: {
        sortBy: "_sortLogin",
        descending: true,
        page: 1,
        rowsPerPage: 25,
      },
      wrestlerAuditPagination: {
        sortBy: "_whenMs",
        descending: true,
        page: 1,
        rowsPerPage: 25,
      },
      matchAuditPagination: {
        sortBy: "_whenMs",
        descending: true,
        page: 1,
        rowsPerPage: 25,
      },
      auditDeleteDialog: false,
      auditDeleteTarget: null,
      auditDeleteSection: "",
      auditDeleting: false,
      migrationLoading: false,
      migrationLastResult: null,
      migrationBatchSize: 40,
    };
  },
  computed: {
    migrationLastResultJson() {
      if (!this.migrationLastResult) return "";
      try {
        return JSON.stringify(this.migrationLastResult, null, 2);
      } catch (_e) {
        return String(this.migrationLastResult);
      }
    },
    auditDeleteSectionLabel() {
      if (this.auditDeleteSection === "match") return "Match audit";
      return "Wrestler profile audit";
    },
    wrestlerLogs() {
      return (this.allAuditLogs || []).filter(
        (l) => l.entityType === "wrestler"
      );
    },
    matchLogs() {
      return (this.allAuditLogs || []).filter((l) => l.entityType === "match");
    },
    usersTableItems() {
      return this.users.map((u) => ({
        ...u,
        _sortName: (this.userDisplayName(u) || "").toLowerCase(),
        _sortLogin: this.userDateMs(u.lastLoginAt),
        _createdMs: this.userDateMs(u.createdAt),
        _name: this.userDisplayName(u),
        _created: this.formatUserDate(u.createdAt) || "—",
        _login: this.formatUserDate(u.lastLoginAt) || "—",
      }));
    },
    wrestlerAuditItems() {
      return this.wrestlerLogs.map((item) => ({
        ...item,
        _when: this.formatAuditDate(item.timestamp) || "—",
        _whenMs: this.auditTimestampMs(item),
        _by: this.formatChangedBy(item),
        _wrestler: this.wrestlerResolvedLabel(item),
        _changeSort: this.formatChangesDetail(item.changes, false) || "",
      }));
    },
    matchAuditItems() {
      return this.matchLogs.map((item) => ({
        ...item,
        _when: this.formatAuditDate(item.timestamp) || "—",
        _whenMs: this.auditTimestampMs(item),
        _by: this.formatChangedBy(item),
        _subject: this.matchSubjectLabel(item),
        _changeSort: this.formatChangesDetail(item.changes, false) || "",
      }));
    },
  },
  async mounted() {
    await Promise.all([this.loadUsers(), this.loadAuditLogs()]);
  },
  methods: {
    async runCanonicalMigrationBatch() {
      this.migrationLoading = true;
      this.migrationLastResult = null;
      try {
        const size = Math.min(
          200,
          Math.max(5, Number(this.migrationBatchSize) || 40)
        );
        const fn = httpsCallable(
          getFunctions(getApp(), "us-central1"),
          "runMatchCanonicalMigrationBatch",
          {
            // Server allows 300s; SDK default is 70s — large batches otherwise
            // fail in the browser as a generic network / fetch error.
            timeout: 330000,
          }
        );
        const res = await fn({ batchSize: size });
        this.migrationLastResult = res.data || {};
        const msg =
          (this.migrationLastResult && this.migrationLastResult.message) ||
          "Batch finished.";
        this.$root.$emit(
          "toast",
          msg,
          this.migrationLastResult.done ? "success" : "info"
        );
      } catch (e) {
        const code = e && e.code ? String(e.code) : "";
        const base =
          e && e.message
            ? String(e.message)
            : "Migration call failed (check sign-in, admin email, indexes, and Cloud Function logs).";
        const msg = code ? `${code}: ${base}` : base;
        this.migrationLastResult = {
          error: msg,
          code: code || undefined,
          message: e && e.message ? String(e.message) : undefined,
          details: e && e.details != null ? e.details : undefined,
        };
        this.$root.$emit("toast", msg, "error");
      } finally {
        this.migrationLoading = false;
      }
    },

    userDisplayName(user) {
      if (!user) return "";
      if (user.displayName) return user.displayName;
      if (user.email) return user.email;
      return user.uid || "";
    },

    wrestlerNameFor(entityId) {
      if (!entityId) return "—";
      const n = this.athleteNameById[entityId];
      if (n) return n;
      return "Unknown wrestler";
    },

    wrestlerResolvedLabel(item) {
      if (!item || !item.entityId) return "—";
      const live = this.athleteNameById[item.entityId];
      if (live) return live;
      const fromLog = wrestlerDisplayNameFromAuditChanges(
        item.changes,
        item.action
      );
      if (fromLog) return fromLog;
      return "Unknown wrestler";
    },

    wrestlerShowRawIdHint(item) {
      if (!item || !item.entityId) return false;
      if (this.athleteNameById[item.entityId]) return false;
      return !wrestlerDisplayNameFromAuditChanges(item.changes, item.action);
    },

    /** Firebase v9 uses `exists` (boolean); some code paths use `exists()`. */
    docSnapExists(snap) {
      if (!snap) return false;
      return typeof snap.exists === "function" ? snap.exists() : !!snap.exists;
    },

    matchSubjectLabel(item) {
      if (!item || !item.entityId) return "—";
      const id = item.entityId;
      const athleteName = this.athleteNameById[id];
      if (athleteName) {
        return `Profile: ${athleteName}`;
      }
      const meta = this.matchMetaById[id];
      if (meta) {
        const parts = [];
        if (meta.opponent) parts.push(`vs ${meta.opponent}`);
        if (meta.matchDateLabel) parts.push(meta.matchDateLabel);
        const detail = parts.length ? parts.join(" · ") : "Match record";
        const whose =
          meta.athleteName ||
          (meta.athleteIds && meta.athleteIds.length > 1
            ? "Multiple wrestlers"
            : "Wrestler");
        return `${detail} (${whose}'s matches)`;
      }
      const fromAudit = matchSummaryFromAuditChanges(item.changes, item.action);
      if (fromAudit) return fromAudit;
      return id;
    },

    matchShowRawIdHint(item) {
      if (!item || !item.entityId) return false;
      if (this.athleteNameById[item.entityId]) return false;
      if (this.matchMetaById[item.entityId]) return false;
      return !matchSummaryFromAuditChanges(item.changes, item.action);
    },

    openAuditDeleteDialog(item, section) {
      if (!item || !item.id) return;
      if (section === "wrestler" && item.entityType !== "wrestler") return;
      if (section === "match" && item.entityType !== "match") return;
      this.auditDeleteTarget = item;
      this.auditDeleteSection = section;
      this.auditDeleteDialog = true;
    },

    closeAuditDeleteDialog() {
      if (this.auditDeleting) return;
      this.auditDeleteDialog = false;
      this.auditDeleteTarget = null;
      this.auditDeleteSection = "";
    },

    async confirmDeleteAuditEntry() {
      const t = this.auditDeleteTarget;
      if (!t || !t.id) return;
      this.auditDeleting = true;
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, "auditLogs", t.id));
        this.allAuditLogs = this.allAuditLogs.filter((l) => l.id !== t.id);
        this.$root.$emit("toast", "Audit entry deleted.", "success");
        this.auditDeleteDialog = false;
        this.auditDeleteTarget = null;
        this.auditDeleteSection = "";
      } catch (e) {
        const msg =
          e && e.message ? e.message : "Failed to delete audit entry.";
        this.$root.$emit("toast", msg, "error");
      } finally {
        this.auditDeleting = false;
      }
    },

    formatChangedBy(item) {
      if (!item) return "—";
      const uid = item.userId;
      const raw = item.userName;
      const name =
        raw != null && String(raw).trim() !== "" ? String(raw).trim() : "";

      if (!uid || uid === "system") {
        return name || "System";
      }

      if (name && name !== uid) {
        return name;
      }

      const row = this.users.find((u) => u.uid === uid);
      if (row) {
        return this.userDisplayName(row);
      }

      if (name) return name;
      return "Unknown user";
    },

    changedByTitle(item) {
      if (!item || !item.userId || item.userId === "system") return "";
      return item.userId;
    },

    auditTimestampMs(item) {
      if (!item) return 0;
      try {
        const t = item.timestamp;
        if (t && typeof t.toDate === "function") return t.toDate().getTime();
        if (item.createdAt) {
          const d = new Date(item.createdAt);
          return isNaN(d.getTime()) ? 0 : d.getTime();
        }
      } catch (_e) {
        return 0;
      }
      return 0;
    },

    async loadUsers() {
      this.usersLoading = true;
      this.users = [];
      try {
        const db = getFirestore();
        const snap = await getDocs(collection(db, "users"));
        const rows = [];
        snap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          rows.push({
            uid: data.uid || docSnap.id,
            email: data.email || "",
            displayName: data.displayName || "",
            providerId: data.providerId || "",
            createdAt: data.createdAt || null,
            lastLoginAt: data.lastLoginAt || null,
          });
        });
        rows.sort((a, b) => {
          const ta = this.userDateMs(a.lastLoginAt);
          const tb = this.userDateMs(b.lastLoginAt);
          if (ta !== tb) return tb - ta;
          const aEmail = (a.email || "").toLowerCase();
          const bEmail = (b.email || "").toLowerCase();
          if (aEmail && bEmail) return aEmail.localeCompare(bEmail);
          return (a.displayName || "").localeCompare(b.displayName || "");
        });
        this.users = rows;
      } catch (_e) {
        this.users = [];
      } finally {
        this.usersLoading = false;
      }
    },

    userDateMs(value) {
      if (!value) return 0;
      try {
        if (typeof value === "string") {
          const d = new Date(value);
          return isNaN(d.getTime()) ? 0 : d.getTime();
        }
        if (value && typeof value.toDate === "function") {
          return value.toDate().getTime();
        }
      } catch (_e) {
        return 0;
      }
      return 0;
    },

    async loadAuditLogs() {
      this.auditLoading = true;
      this.allAuditLogs = [];
      this.athleteNameById = {};
      this.matchMetaById = {};
      try {
        const db = getFirestore();
        const snap = await getDocs(collection(db, "auditLogs"));
        const rows = [];
        snap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          rows.push({
            id: docSnap.id,
            ...data,
          });
        });

        rows.sort((a, b) => {
          const timeA =
            a.timestamp && a.timestamp.toDate
              ? a.timestamp.toDate().getTime()
              : a.createdAt
              ? new Date(a.createdAt).getTime()
              : 0;
          const timeB =
            b.timestamp && b.timestamp.toDate
              ? b.timestamp.toDate().getTime()
              : b.createdAt
              ? new Date(b.createdAt).getTime()
              : 0;
          return timeB - timeA;
        });

        this.allAuditLogs = rows;
        await this.resolveAuditEntityLabels(rows);
      } catch (_e) {
        this.allAuditLogs = [];
      } finally {
        this.auditLoading = false;
      }
    },

    async fetchAthleteNames(db, ids) {
      const map = { ...this.athleteNameById };
      const unique = [...new Set(ids.filter(Boolean))];
      const chunk = 40;
      for (let i = 0; i < unique.length; i += chunk) {
        const slice = unique.slice(i, i + chunk);
        await Promise.all(
          slice.map(async (id) => {
            if (map[id]) return;
            try {
              const s = await getDoc(doc(db, "athletes", id));
              if (this.docSnapExists(s)) {
                const d = s.data() || {};
                map[id] =
                  d.FullName ||
                  [d.FirstName, d.LastName].filter(Boolean).join(" ").trim() ||
                  id;
              }
            } catch (_e) {
              /* ignore */
            }
          })
        );
      }
      this.athleteNameById = map;
    },

    async resolveMatchDocsByIds(db, matchIds) {
      const meta = { ...this.matchMetaById };
      const ids = [...new Set(matchIds.filter(Boolean))];
      for (let i = 0; i < ids.length; i++) {
        const mid = ids[i];
        if (meta[mid]) continue;
        try {
          const s = await getDoc(doc(db, MATCHES_CANONICAL_COLLECTION, mid));
          const exists =
            typeof s.exists === "function" ? s.exists() : s.exists;
          if (!exists) continue;
          const d = s.data() || {};
          const w = d.WinnerAthleteId;
          const l = d.LoserAthleteId;
          const oppLabel =
            [d.OpponentLabelWinnerDoc, d.OpponentLabelLoserDoc]
              .map((x) => (x != null ? String(x).trim() : ""))
              .filter(Boolean)
              .join(" · ") || "—";
          meta[mid] = {
            athleteIds: [w, l].filter(Boolean),
            opponent: oppLabel,
            matchDateLabel: this.formatAuditValue(d.MatchDate),
          };
        } catch (_e) {
          /* permission or missing doc */
        }
      }
      this.matchMetaById = meta;

      const needAthletes = new Set();
      Object.values(meta).forEach((m) => {
        (m.athleteIds || []).forEach((aid) => needAthletes.add(aid));
      });
      await this.fetchAthleteNames(db, [...needAthletes]);

      const map = { ...this.matchMetaById };
      Object.keys(map).forEach((mid) => {
        const m = map[mid];
        if (m && m.athleteIds && m.athleteIds.length) {
          const names = m.athleteIds.map(
            (aid) => this.athleteNameById[aid] || aid
          );
          m.athleteName =
            names.length > 2
              ? `${names.slice(0, 2).join(", ")} +${names.length - 2}`
              : names.join(", ");
        }
      });
      this.matchMetaById = map;
    },

    async resolveAuditEntityLabels(logs) {
      const db = getFirestore();
      const wrestlerIds = new Set();
      const matchEntityIds = new Set();
      logs.forEach((l) => {
        if (l.entityType === "wrestler" && l.entityId)
          wrestlerIds.add(l.entityId);
        if (l.entityType === "match" && l.entityId)
          matchEntityIds.add(l.entityId);
      });

      await this.fetchAthleteNames(db, [...wrestlerIds, ...matchEntityIds]);

      const unresolvedMatchIds = [...matchEntityIds].filter(
        (id) => !this.athleteNameById[id]
      );
      if (unresolvedMatchIds.length > 0) {
        await this.resolveMatchDocsByIds(db, unresolvedMatchIds);
      }
    },

    formatUserDate(value) {
      if (!value) return "";
      try {
        if (typeof value === "string") {
          const d = new Date(value);
          if (!isNaN(d.getTime())) return d.toLocaleString();
        }
        if (value && typeof value.toDate === "function") {
          return value.toDate().toLocaleString();
        }
        return String(value);
      } catch (_e) {
        return "";
      }
    },

    formatAuditDate(timestamp) {
      if (!timestamp) return "";
      try {
        const val =
          typeof timestamp.toDate === "function"
            ? timestamp.toDate()
            : timestamp instanceof Date
            ? timestamp
            : new Date(timestamp);
        return val.toLocaleString();
      } catch (_e) {
        return "";
      }
    },

    getActionColor(action) {
      switch (action) {
        case "create":
          return "#4CAF50";
        case "update":
          return "#FF9800";
        case "delete":
          return "#F44336";
        default:
          return "#9E9E9E";
      }
    },

    formatAuditValue(val) {
      if (val === null || val === undefined || val === "") return "—";
      if (val && typeof val.toDate === "function") {
        try {
          return val.toDate().toLocaleString();
        } catch (_e) {
          return String(val);
        }
      }
      if (val instanceof Date) return val.toLocaleString();
      if (typeof val === "object") return JSON.stringify(val);
      return String(val);
    },

    formatChangesDetail(changes, full) {
      if (!changes || typeof changes !== "object") return "";
      const keys = Object.keys(changes);
      if (keys.length === 0) return "";
      const parts = keys.map((field) => {
        const label = FIELD_LABELS[field] || field;
        const c = changes[field];
        if (!c || typeof c !== "object" || !("old" in c || "new" in c)) {
          return `${label}: ${this.formatAuditValue(c)}`;
        }
        return `${label}: ${this.formatAuditValue(
          c.old
        )} → ${this.formatAuditValue(c.new)}`;
      });
      const joined = parts.join(full ? "\n" : "; ");
      if (full) return joined;
      const maxLen = 140;
      return joined.length <= maxLen ? joined : `${joined.slice(0, maxLen)}…`;
    },
  },
};
</script>

<style scoped>
.mb-3 {
  margin-bottom: 16px;
}

/* Theme tokens: admin page follows app light/dark (tables stay readable). */
.admin-dashboard {
  --adm-page-text: #1a1a1a;
  --adm-card-bg: #ffffff;
  --adm-card-text: #1a1a1a;
  --adm-desc: #424242;
  --adm-table-shell-border: #cfd4dc;
  --adm-table-shell-bg: #fff;
  --adm-table-bg: #fff;
  --adm-thead-grad-start: #eef1f5;
  --adm-thead-grad-end: #e2e6ec;
  --adm-th-text: #263238;
  --adm-th-border-bottom: #b0bec5;
  --adm-th-border-right: #cfd8dc;
  --adm-row-bg: #ffffff;
  --adm-row-even: #f4f6f8;
  --adm-row-hover: #ffebee;
  --adm-td-text: #1a1a1a;
  --adm-td-border-bottom: #dce0e6;
  --adm-td-border-right: #e8eaef;
  --adm-actions-bg: #eceff1;
  --adm-actions-text: #1a1a1a;
  --adm-actions-icon: #37474f;
  --adm-emphasis: #0d47a1;
  --adm-id-code-bg: #eceff1;
  --adm-id-code-text: #37474f;
  --adm-id-code-border: #cfd8dc;
  --adm-dash: #78909c;
  --adm-empty: #546e7a;
  --adm-inline-bg: #eceff1;
  --adm-inline-text: #263238;
  --adm-changes-cell: #1a1a1a;
  --adm-migration-input-text: #0d1117;
  --adm-migration-slot-bg: #fff;
  --adm-migration-hint: #546e7a;
  --adm-pre-bg: #f5f5f5;
  --adm-pre-border: #e0e0e0;
  --adm-pre-text: #263238;
  --adm-delete-preview-bg: #f5f5f5;
  --adm-delete-preview-text: #37474f;
  color: var(--adm-page-text);
}

.admin-dashboard--dark {
  --adm-page-text: #e5e7eb;
  --adm-card-bg: #1e1e1e;
  --adm-card-text: #e5e7eb;
  --adm-desc: #b0bec5;
  --adm-table-shell-border: #424242;
  --adm-table-shell-bg: #252525;
  --adm-table-bg: #252525;
  --adm-thead-grad-start: #333;
  --adm-thead-grad-end: #2d2d2d;
  --adm-th-text: #e0e0e0;
  --adm-th-border-bottom: #616161;
  --adm-th-border-right: #424242;
  --adm-row-bg: #252525;
  --adm-row-even: #2c2c2c;
  --adm-row-hover: #402828;
  --adm-td-text: #e5e7eb;
  --adm-td-border-bottom: #424242;
  --adm-td-border-right: #383838;
  --adm-actions-bg: #2d2d2d;
  --adm-actions-text: #e0e0e0;
  --adm-actions-icon: #b0bec5;
  --adm-emphasis: #90caf9;
  --adm-id-code-bg: #374151;
  --adm-id-code-text: #e5e7eb;
  --adm-id-code-border: #4b5563;
  --adm-dash: #9ca3af;
  --adm-empty: #9ca3af;
  --adm-inline-bg: #374151;
  --adm-inline-text: #e5e7eb;
  --adm-changes-cell: #e5e7eb;
  --adm-migration-input-text: #f9fafb;
  --adm-migration-slot-bg: #2d2d2d;
  --adm-migration-hint: #9ca3af;
  --adm-pre-bg: #121212;
  --adm-pre-border: #424242;
  --adm-pre-text: #e0e0e0;
  --adm-delete-preview-bg: #2d2d2d;
  --adm-delete-preview-text: #e0e0e0;
}

.admin-dashboard >>> .v-card {
  background: var(--adm-card-bg) !important;
  color: var(--adm-card-text) !important;
}

.admin-dashboard >>> .v-card__text {
  color: var(--adm-card-text) !important;
}

.rounded-card {
  border-radius: 8px;
  overflow: hidden;
}

.admin-section-desc {
  margin-bottom: 16px;
  color: var(--adm-desc) !important;
  font-size: 14px;
  line-height: 1.45;
  max-width: 56rem;
}

.admin-section-desc code {
  background: var(--adm-inline-bg);
  color: var(--adm-inline-text);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.9em;
}

.admin-table-shell {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--adm-table-shell-border);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  background: var(--adm-table-shell-bg);
}

.admin-dashboard--dark .admin-table-shell {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}

.admin-data-table {
  background: var(--adm-table-bg) !important;
  color: var(--adm-td-text) !important;
}

/* Vuetify 1.x data table innards */
.admin-data-table >>> .v-table {
  background: var(--adm-table-bg) !important;
  color: var(--adm-td-text) !important;
}

.admin-data-table >>> table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  color: var(--adm-td-text) !important;
}

.admin-data-table >>> thead tr {
  background: linear-gradient(
    180deg,
    var(--adm-thead-grad-start) 0%,
    var(--adm-thead-grad-end) 100%
  );
}

.admin-data-table >>> thead th {
  font-weight: 600 !important;
  font-size: 11px !important;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--adm-th-text) !important;
  border-bottom: 2px solid var(--adm-th-border-bottom) !important;
  border-right: 1px solid var(--adm-th-border-right) !important;
  padding: 10px 12px !important;
  white-space: nowrap;
  vertical-align: bottom;
}

.admin-data-table >>> thead th:last-of-type {
  border-right: none !important;
}

.admin-data-table >>> thead th.sortable:hover {
  color: #b71c1c !important;
}

.admin-data-table >>> tbody tr.admin-table-row {
  color: var(--adm-td-text) !important;
  background-color: var(--adm-row-bg);
}

.admin-data-table >>> tbody tr.admin-table-row:nth-child(even) {
  background-color: var(--adm-row-even);
}

.admin-data-table >>> tbody tr.admin-table-row:hover {
  background-color: var(--adm-row-hover) !important;
}

.admin-data-table >>> tbody td {
  padding: 8px 12px !important;
  font-size: 14px;
  line-height: 1.35;
  color: var(--adm-td-text) !important;
  border-bottom: 1px solid var(--adm-td-border-bottom) !important;
  border-right: 1px solid var(--adm-td-border-right) !important;
  vertical-align: middle;
  height: auto;
}

.admin-data-table >>> tbody td:last-of-type {
  border-right: none !important;
}

.admin-data-table >>> .v-datatable__actions {
  border-top: 1px solid var(--adm-table-shell-border);
  background: var(--adm-actions-bg);
  padding: 8px 10px;
  font-size: 13px;
  color: var(--adm-actions-text) !important;
}

.admin-data-table >>> .v-datatable__actions .v-btn,
.admin-data-table >>> .v-datatable__actions .v-icon {
  color: var(--adm-actions-icon) !important;
}

.admin-cell-nowrap {
  white-space: nowrap;
}

.admin-cell-emphasis {
  font-weight: 600;
  color: var(--adm-emphasis) !important;
}

.admin-cell-mono {
  font-family: Consolas, "Roboto Mono", monospace;
  font-size: 13px;
  color: var(--adm-td-text) !important;
}

.admin-cell-by {
  max-width: 220px;
}

.admin-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.admin-id-code {
  display: block;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 4px;
  background: var(--adm-id-code-bg);
  color: var(--adm-id-code-text) !important;
  border: 1px solid var(--adm-id-code-border);
  word-break: break-all;
}

.admin-provider-chip {
  margin: 0;
}

.admin-action-chip {
  margin: 0;
  text-transform: capitalize;
}

.admin-dash {
  color: var(--adm-dash) !important;
}

.admin-empty {
  padding: 28px 16px;
  text-align: center;
  color: var(--adm-empty) !important;
  font-size: 14px;
}

.changes-cell {
  max-width: 360px;
  min-width: 120px;
  font-size: 13px;
  line-height: 1.35;
  color: var(--adm-changes-cell) !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.admin-actions-cell {
  width: 1%;
  white-space: nowrap;
  text-align: center !important;
  vertical-align: middle !important;
}

.admin-inline-code {
  padding: 2px 6px;
  background: var(--adm-inline-bg);
  color: var(--adm-inline-text);
  border-radius: 4px;
  font-size: 0.85em;
}

/* Readable digits in outline number field (focus / selection contrast). */
.admin-migration-batch-field input[type="number"] {
  color: var(--adm-migration-input-text) !important;
  -webkit-text-fill-color: var(--adm-migration-input-text) !important;
  caret-color: #b71c1c;
  font-weight: 600;
  font-size: 16px;
}

.admin-migration-batch-field .v-input__slot {
  background: var(--adm-migration-slot-bg) !important;
}

.admin-migration-batch-field.v-input--is-focused .v-input__slot {
  background: var(--adm-migration-slot-bg) !important;
}

.admin-migration-batch-field .v-messages {
  color: var(--adm-migration-hint) !important;
  font-size: 12px;
  line-height: 1.35;
}

.admin-migration-result {
  margin-top: 12px;
  padding: 12px;
  background: var(--adm-pre-bg);
  border-radius: 6px;
  border: 1px solid var(--adm-pre-border);
  max-height: 220px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--adm-pre-text) !important;
}

.admin-delete-preview {
  margin-top: 8px;
  padding: 10px 12px;
  background: var(--adm-delete-preview-bg);
  border-radius: 6px;
  line-height: 1.5;
  color: var(--adm-delete-preview-text) !important;
}

.admin-id-code--inline {
  display: inline;
  margin-left: 6px;
  font-size: 11px;
  padding: 2px 6px;
  vertical-align: middle;
}
</style>
